import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { MatTabsModule } from '@angular/material/tabs'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatFormField, MatFormFieldModule, MatLabel } from '@angular/material/form-field'
import { MatInput, MatInputModule } from '@angular/material/input'
import { MatSelectModule } from '@angular/material/select'
import { MatDatepickerModule } from '@angular/material/datepicker'
import { MatNativeDateModule } from '@angular/material/core'
import { MatProgressSpinner } from '@angular/material/progress-spinner'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { MatTooltipModule } from '@angular/material/tooltip'
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask'
import moment from 'moment/moment'

import { FornecedorGestaoService } from '../../../core/services/fornecedor-gestao/fornecedor-gestao.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { FornecedorMotoristaVm } from '../../../core/models/supplier-portal.model'
import { ErrorRequestModel } from '../../../core/models/response.model'
import { cpfValidator } from '../../../shared/utils/cpf_validator'

export interface AssignDriverGestaoResult {
  success: boolean
  avisos: string[]
}

// Versão "TG operando em nome do fornecedor" do modal-assign-driver do Portal do
// Fornecedor — mesmas regras de negócio, mas o fornecedor vem explícito por
// {fornecedorId} (nunca do usuário logado) e não há upload de documentos aqui
// (nenhuma rota de documento foi documentada pro controller FornecedorGestao).
// Cadastro é find-or-create: se o CPF já existir, só vincula ao fornecedor.
@Component({
  selector: 'modal-assign-driver-gestao',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FormsModule,
    MatTabsModule,
    MatButtonModule,
    MatIconModule,
    MatFormField, MatInput, MatLabel,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatProgressSpinner,
    MatSlideToggle,
    MatTooltipModule,
    NgxMaskDirective,
  ],
  templateUrl: './modal-assign-driver-gestao.component.html',
  styleUrl: './modal-assign-driver-gestao.component.scss',
  providers: [provideNgxMask()],
})
export class ModalAssignDriverGestaoComponent implements OnInit {
  @Output() response = new EventEmitter<AssignDriverGestaoResult>()

  constructor(
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: { osId: string; fornecedorId: number; nomeFornecedor?: string },
    private dialogRef: MatDialogRef<ModalAssignDriverGestaoComponent>,
    private fornecedorGestaoService: FornecedorGestaoService,
    private snackbar: SnackbarCustomService,
  ) {}

  listCategory: any[] = [
    { id: 'A', description: 'A' },
    { id: 'B', description: 'B' },
    { id: 'C', description: 'C' },
    { id: 'D', description: 'D' },
    { id: 'E', description: 'E' },
  ]

  // Aba "Motoristas do fornecedor"
  drivers: FornecedorMotoristaVm[] = []
  loadingList = false
  assigningId: number | null = null

  cpfSearch = ''
  searchingCpf = false
  cpfResult: FornecedorMotoristaVm | null = null
  cpfNotFound = false

  // Aba "Cadastrar novo / vincular por CPF"
  form!: FormGroup
  creating = false

  ngOnInit(): void {
    this.createForm()
    this.loadDrivers()
  }

  private createForm(): void {
    this.form = this.fb.group({
      nome: ['', Validators.required],
      cpf: ['', [Validators.required, cpfValidator()]],
      contato: ['', Validators.required],
      categoria: [''],
      numeroRegistro: [''],
      validade: ['', Validators.required],
      antecedentesCriminais: [false],
      antecedentesValidade: [''],
    })
  }

  loadDrivers(): void {
    this.loadingList = true
    this.fornecedorGestaoService.listPagedMotoristas(this.data.fornecedorId, 1, 50).subscribe({
      next: (res: any) => { this.drivers = res.data ?? [] },
      error: (err: ErrorRequestModel) => { this.loadingList = false; this.snackbar.errorRequest(err) },
      complete: () => { this.loadingList = false },
    })
  }

  driverBlocked(driver: FornecedorMotoristaVm): boolean {
    return !!driver.cnhVencida
  }

  assignExisting(motoristaId: number): void {
    this.assigningId = motoristaId
    this.fornecedorGestaoService.atribuirMotorista(this.data.osId, motoristaId).subscribe({
      next: (res: any) => {
        this.snackbar.success(res.message)
        this.response.emit({ success: true, avisos: res.data?.avisos ?? [] })
        this.dialogRef.close()
      },
      error: (err: ErrorRequestModel) => { this.assigningId = null; this.snackbar.errorRequest(err) },
    })
  }

  searchByCpf(): void {
    const cpf = this.cpfSearch.replace(/\D/g, '')
    if (cpf.length === 0) return
    this.searchingCpf = true
    this.cpfResult = null
    this.cpfNotFound = false
    this.fornecedorGestaoService.getMotoristaByCpf(this.data.fornecedorId, cpf).subscribe({
      next: (res: any) => { this.cpfResult = res.data },
      error: () => {
        this.cpfNotFound = true
        this.form.get('cpf')?.setValue(this.cpfSearch)
      },
      complete: () => { this.searchingCpf = false },
    })
  }

  clearCpfSearch(): void {
    this.cpfSearch = ''
    this.cpfResult = null
    this.cpfNotFound = false
  }

  onCpfInputChange(): void {
    if (!this.cpfSearch) {
      this.cpfResult = null
      this.cpfNotFound = false
    }
  }

  // Já vinculado a este fornecedor — atribui direto. Se não, o cadastro (find-or-create)
  // na aba ao lado é quem vincula.
  createAndAssign(): void {
    if (this.form.invalid) return
    this.creating = true
    const v = this.form.value
    const model = {
      nome: v.nome,
      cpf: (v.cpf || '').replace(/\D/g, ''),
      contato: v.contato,
      tipoContatoId: 4,
      numeroRegistro: v.numeroRegistro || '',
      categoria: v.categoria || '',
      validade: v.validade ? moment(v.validade).format('DD/MM/YYYY') : '',
      antecedentesCriminais: v.antecedentesCriminais ? 'S' : 'N',
      antecedentesValidade: v.antecedentesValidade ? moment(v.antecedentesValidade).format('DD/MM/YYYY') : '',
    }
    this.fornecedorGestaoService.createMotorista(this.data.fornecedorId, model).subscribe({
      next: (res: any) => {
        this.creating = false
        const motoristaId = res.data?.id
        if (motoristaId) {
          this.assignExisting(motoristaId)
        } else {
          this.snackbar.success(res.message)
          this.response.emit({ success: true, avisos: [] })
          this.dialogRef.close()
        }
      },
      error: (err: ErrorRequestModel) => { this.creating = false; this.snackbar.errorRequest(err) },
    })
  }

  close(): void { this.dialogRef.close() }
}
